import {Dispatch, RefObject, SetStateAction, useCallback} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {
    CardNetworkAsset,
    HeadlessVaultManager,
    ICardPaymentMethodManager,
} from '@primer-io/checkout-web';
import {AxiosError} from 'axios';
import {useDispatch, useSelector} from 'react-redux';

import {CHECKOUT_PAYMENT_STEP} from 'constants/analytics/events/checkout';
import {useHasGlassesCare} from 'desktop/hooks/useHasGlassesCare/useHasGlassesCare';
import {PAYMENTS} from 'desktop/pages/checkout/constants/paymentStep';
import {useGetPrimerToken} from 'desktop/pages/checkout/hooks/primer/useGetPrimerToken';
import {setUiMessage} from 'desktop/pages/checkout/store/reducers/primer/primer';
import {
    getIsSaveCardChecked,
    getVaultPaymentId,
} from 'desktop/pages/checkout/store/selectors/primer/primer';
import {getErrorModified} from 'desktop/utils/sendPrimerErrorEvent/sendPrimerErrorEvent';
import {validateOrder} from 'libs/checkout/requests/validatePrimerOrder';
import {getDefaultErrorMessage} from 'libs/retryError/retryError';
import {logWithSentry} from 'libs/sentry/sentry';

import {PrimerErrorType, defaultErrorState} from '../constants';

import {useHandleOrderCompletion} from './useHandleOrderCompletion';

const getAnalyticData = (error: AxiosError<{diagnostic_code?: string}>) => {
    return {
        code: error.response?.data?.diagnostic_code || error.code || '',
        status: error.response?.status || 'Unknown',
    };
};

/**
 *  hook for submitting primer credit card form
 */
export const useSubmitPrimerCreditCard = ({
    cardNetwork,
    managerRef,
    setPrimerError,
    vaultManagerRef,
}: {
    cardNetwork: CardNetworkAsset | null;
    managerRef: RefObject<ICardPaymentMethodManager | null>;
    vaultManagerRef: RefObject<HeadlessVaultManager | null>;
    setPrimerError: Dispatch<SetStateAction<PrimerErrorType>>;
}) => {
    const {token} = useGetPrimerToken();
    const vaultPaymentId = useSelector(getVaultPaymentId);
    const vaultOnSuccess = useSelector(getIsSaveCardChecked);
    const hasGlassesCare = useHasGlassesCare();
    const dispatch = useDispatch();

    const {setLoading} = useHandleOrderCompletion();

    const tryToPrevalidateOrder = useCallback(async () => {
        try {
            return await validateOrder(
                PAYMENTS.METHOD_PRIMER,
                token || '',
                vaultPaymentId ? false : hasGlassesCare || vaultOnSuccess,
            );
        } catch (error) {
            const analyticData = getAnalyticData(error);
            const errorMessage = error.response?.data?.messages?.error?.[0]?.message;
            sendDesktopAnalyticsEvent(CHECKOUT_PAYMENT_STEP, {
                message: `Error - prevalidate - ${getErrorModified(errorMessage || '')} - code - ${analyticData.code} - response status - ${analyticData.status} - Place Order CTA`,
                method: 'Primer Credit Card',
            });
            dispatch(setUiMessage(errorMessage || getDefaultErrorMessage(error)));
            setLoading(false);
            return null;
        }
    }, [token, vaultPaymentId, hasGlassesCare, setLoading, vaultOnSuccess, dispatch]);

    const submitFormWithSavedCard = useCallback(async () => {
        if (!(await tryToPrevalidateOrder())) return;
        setPrimerError(defaultErrorState);
        await vaultManagerRef.current?.startPaymentFlow(vaultPaymentId);
    }, [setPrimerError, tryToPrevalidateOrder, vaultManagerRef, vaultPaymentId]);

    const validateForm = useCallback(async () => {
        if (!managerRef.current) return false;
        const {valid, validationErrors} = await managerRef.current.validate();
        if (validationErrors.length) {
            const primerErrors: Partial<PrimerErrorType> = validationErrors.reduce((acc, error) => {
                return {...acc, [error.name]: error.message};
            }, {});
            setPrimerError(prevState => {
                return {...prevState, ...primerErrors};
            });
            sendDesktopAnalyticsEvent(CHECKOUT_PAYMENT_STEP, {
                message: `Error - ${
                    primerErrors['cardNumber-card'] ||
                    primerErrors['expiryDate-card'] ||
                    primerErrors['cvv-card']
                }`,
                method: 'Primer Credit Card',
            });
        }
        return valid;
    }, [managerRef, setPrimerError]);

    return useCallback(async () => {
        // call `PrimerCardManager.submit() or vaultManager.startPaymentFlow()` when form is submitted
        setLoading(true);
        if (vaultPaymentId) await submitFormWithSavedCard();
        else if (await validateForm()) {
            if (!(await tryToPrevalidateOrder())) return;
            setPrimerError(defaultErrorState);
            try {
                await managerRef.current!.submit({cardNetwork: cardNetwork?.displayName});
            } catch (error) {
                setLoading(false);
                logWithSentry(error, 'Error submitting form with primer');
            }
        } else setLoading(false);
    }, [
        vaultPaymentId,
        submitFormWithSavedCard,
        validateForm,
        tryToPrevalidateOrder,
        setPrimerError,
        managerRef,
        cardNetwork?.displayName,
        setLoading,
    ]);
};
